import { Component, ChangeDetectionStrategy, Input, ViewChild, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil, throttleTime } from 'rxjs/operators';
import { AbstractTuiControl, TuiMultiSelectComponent } from '@taiga-ui/legacy';
import { TuiIdentityMatcher, TuiStringHandler } from '@taiga-ui/cdk';
import { TuiSizeL, TuiSizeS } from '@taiga-ui/core';
import { Nullable } from '@src/types/utils';
import { NumeralPluralsPipe } from '@src/app/shared/pipes';
import { LoyaltyService } from '@src/api';

import { LoyaltyProgramListProduct } from '../models';
import { convertToProductList } from '../services/utils/convertToProductList';

/**
 * Input для вставки в форму, если нужно выбрать объявления
 *
 * @example
 * <lp-search-products-input
 *   formControlName="selectedProducts"
 *   label="Объявления"
 *   [pluralize]="['объявление', 'объявления', 'объявлений']"
 * ></lp-search-products-input>
 */
@Component({
  selector: 'lp-search-products-input',
  templateUrl: './lp-search-products-input.component.html',
  styleUrls: ['./lp-search-products-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpSearchProductsInputComponent extends AbstractTuiControl<LoyaltyProgramListProduct[]> {
  private readonly loyaltyService = inject(LoyaltyService);
  private readonly plural = inject(NumeralPluralsPipe);

  /** Лейбл инпута */
  @Input() label = '';

  @Input() pluralize: string[] = [];

  /** Id объявлений, исключённых из поиска */
  @Input() excludeItemIds?: string[];

  @Input() textfieldSize: TuiSizeL | TuiSizeS = 'm';

  @ViewChild(TuiMultiSelectComponent)
  private readonly multipleInputRef?: TuiMultiSelectComponent<LoyaltyProgramListProduct[]>;

  private readonly destroyed$$ = new Subject<void>();

  readonly search$ = new BehaviorSubject<string>('');
  readonly stringify: TuiStringHandler<LoyaltyProgramListProduct> = item => item.name ?? '';
  readonly matcher: TuiIdentityMatcher<LoyaltyProgramListProduct> = (item1, item2) => item1.id === item2.id;

  readonly items$ = this.search$.pipe(
    takeUntil(this.destroyed$$),
    debounceTime(500),
    throttleTime(500),
    startWith(''),
    switchMap(name => this.loyaltyService.searchForLoyaltyProduct({ name, start: 0, top: 1000 })),
    map(items => items.map(item => convertToProductList(item))),
  );

  get title(): string {
    if (this.pluralize.length > 0 && Array.isArray(this.value) && this.value.length > 0) {
      return this.plural.transform(this.value.length, this.pluralize);
    }

    return this.label;
  }

  get focused(): boolean {
    if (this.multipleInputRef) {
      return this.multipleInputRef.focused;
    }

    return false;
  }

  protected getFallbackValue(): LoyaltyProgramListProduct[] {
    return [];
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.search('');
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  setValue(value: LoyaltyProgramListProduct[]) {
    this.value = value;
  }

  getValue() {
    return this.value;
  }

  search(search: Nullable<string>) {
    this.search$.next(search ?? '');
  }
}
